import * as React from 'react';
import { Customer } from 'types/Customer';

import { UploadedFileColumn } from './column-mapping';
import { Matched, NoCustomer, Success } from './messages';
import { MultipleColumns } from './MultipleColumns';
import { SingleColumn } from './SingleColumn';

interface ColumnAssignment {
  kind: 'column';
  columns: UploadedFileColumn[];
}

interface DropdownAssignment {
  kind: 'dropdown';
  customer: Customer | null;
}

interface NoAssignment {
  kind: 'none';
}

/** This type is used for our callback to update the parent */
export type Assignment = NoAssignment | ColumnAssignment | DropdownAssignment;

interface Props {
  columns: UploadedFileColumn[];
  /** Fired whenever the assignment changes */
  onAssignment: (assignment: Assignment) => void;
  fileName: string;
}

interface State {
  columns: UploadedFileColumn[];
  customer: Customer | null;
}

/**
 * Renders the UI for assigning a customer to an uploaded file of containers.
 * The exact UI rendered is conditioned to the number of columns; if there is only a single
 * column that implies there are _only_ container numbers and so we don't want to show the
 * column mapping component.
 */
export const CustomerAssignment: React.FC<Props> = (props) => {
  const { onAssignment } = props;

  // We copy in the columns so we can modify them freely
  const [state, setState] = React.useState<State>({ columns: props.columns, customer: null });

  // Update the parent component
  React.useEffect(() => {
    const hasColumnMapping = !!state.columns.find((c) => c.mapping === 'customer');
    if (hasColumnMapping) {
      onAssignment({ kind: 'column', columns: state.columns });
    } else if (state.customer) {
      onAssignment({ kind: 'dropdown', customer: state.customer });
    } else {
      onAssignment({ kind: 'none' });
    }
  }, [state, onAssignment]);

  // Used to determine if we show the column mapping component and adjust messaging
  const singleColumn = state.columns.length === 1;

  // Find out if we have a column mapped to the customer id upon mount
  const hasCustomerMappedColumn = React.useMemo(() => {
    return !!state.columns.find((c) => c.mapping === 'customer');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const message = hasCustomerMappedColumn ? (
    <Matched />
  ) : (
    <NoCustomer singleColumn={singleColumn} />
  );

  const handleSetCustomer = React.useCallback(
    (customer: Customer | null) => {
      // When a user sets a customer we want to clear any customer column mappings
      setState((s) =>
        customer
          ? {
              ...s,
              columns: s.columns.map((c) =>
                c.mapping === 'customer' ? { ...c, mapping: null } : c
              ),
              customer,
            }
          : { ...s, customer }
      );
    },
    [setState]
  );

  const handleSetColumns = React.useCallback(
    (columns: UploadedFileColumn[]) => {
      setState((s) => ({ ...s, columns, customer: null }));
    },
    [setState]
  );

  const inner = singleColumn ? (
    <SingleColumn setCustomer={handleSetCustomer} />
  ) : (
    <MultipleColumns
      columns={state.columns}
      customer={state.customer}
      setColumns={handleSetColumns}
      setCustomer={handleSetCustomer}
    />
  );

  return (
    <div className="px-8 space-y-8 pb-8">
      <div className="space-y-1">
        <Success fileName={props.fileName} />
        {message}
      </div>
      {inner}
    </div>
  );
};
CustomerAssignment.displayName = 'CustomerAssignment';
